import { AlignLeft, AlignRight, MonitorSmartphone, Moon, Sun, Wand2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/contexts/ThemeContext";
import { useDirection } from "@/contexts/DirectionContext";
import { ThemeCard, DirectionCard } from "@/components/settings/Cards";

/**
 * AppearanceSection — theme picker (Daylight / Moonlit / Auto) and
 * layout direction picker (Auto / LTR / RTL). Both wire directly into
 * the ThemeContext and DirectionContext.
 */
export default function AppearanceSection() {
  const { t } = useTranslation();
  const { theme, preference: themePref, setTheme } = useTheme();
  const { preference: dirPref, effective: dirEff, setPreference: setDirPref } = useDirection();

  return (
    <>
      <div className="card-flat p-6">
        <div className="label-eyebrow mb-4">{t("settings.appearance")}</div>
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">
          {t("settings.appearance_hint")}
        </p>
        <div className="grid grid-cols-3 gap-3">
          <ThemeCard
            active={themePref === "light"}
            onClick={() => setTheme("light")}
            testid="settings-theme-light"
            icon={<Sun size={16}/>}
            label={t("settings.theme_light")}
            hint={t("settings.theme_light_hint")}
            swatches={["#FDFCFB", "#2D5A40", "#E09E50"]}
          />
          <ThemeCard
            active={themePref === "dark"}
            onClick={() => setTheme("dark")}
            testid="settings-theme-dark"
            icon={<Moon size={16}/>}
            label={t("settings.theme_dark")}
            hint={t("settings.theme_dark_hint")}
            swatches={["#14201A", "#E09E50", "#7BC490"]}
          />
          <ThemeCard
            active={themePref === "auto"}
            onClick={() => setTheme("auto")}
            testid="settings-theme-auto"
            icon={<MonitorSmartphone size={16}/>}
            label={t("settings.theme_auto")}
            hint={t("settings.theme_auto_hint", { theme })}
            swatches={["#FDFCFB", "#14201A", "#E09E50"]}
          />
        </div>
      </div>

      <div className="card-flat p-6">
        <div className="label-eyebrow mb-4">{t("settings.direction")}</div>
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">
          {t("settings.direction_hint", { dir: dirEff })}
        </p>
        <div className="grid grid-cols-3 gap-2">
          <DirectionCard
            active={dirPref === "auto"}
            onClick={() => setDirPref("auto")}
            testid="settings-dir-auto"
            icon={<Wand2 size={14}/>}
            label={t("settings.direction_auto")}
            hint={t("settings.direction_auto_hint")}
          />
          <DirectionCard
            active={dirPref === "ltr"}
            onClick={() => setDirPref("ltr")}
            testid="settings-dir-ltr"
            icon={<AlignLeft size={14}/>}
            label={t("settings.direction_ltr")}
            hint={t("settings.direction_ltr_hint")}
          />
          <DirectionCard
            active={dirPref === "rtl"}
            onClick={() => setDirPref("rtl")}
            testid="settings-dir-rtl"
            icon={<AlignRight size={14}/>}
            label={t("settings.direction_rtl")}
            hint={t("settings.direction_rtl_hint")}
          />
        </div>
      </div>
    </>
  );
}
